import {AudioSource} from "../components/audio/audioSource";
import {AnimationSoundPlayer} from "../components/animation/animationSoundPlayer";
import {clamp, clamp01, deltaAngle, invLerp} from "../engine/utils/math";
import {Position} from "../components/transform/position";
import {TensionSound} from "../components/inputObject/tensionSound";
import {MoveGrabState} from "../components/grab/moveGrabState";
import {BaseLevel} from "../components/physics/baseLevel";
import {Crank} from "../components/inputObject/crank";
import {PumpPressure} from "../components/inputObject/pumpPressure";
import {AnimationPlayer} from "../components/animation/animationPlayer";
import {Input} from "../components/state/input";
import {Button, ButtonEvent} from "../components/inputObject/button";
import {AppState} from "../app";
import {PumpSound} from "../components/inputObject/pumpSound";
import {Howler} from '../howler';

export function updateAudio({interactiveCanvas, entities, time, soundEvents, animations}: AppState) {

    if(!Howler._audioUnlocked)
        return

    entities.forEach([ AudioSource], (e) => {

        if (e.AudioSource.instance === undefined) {
            const event = soundEvents[e.AudioSource.soundId]

            if(event)
                e.AudioSource.instance = soundEvents[e.AudioSource.soundId].play()
            else
                console.log("audio id not found:",e.AudioSource.soundId)
        }
    })
    entities.forEach([ AnimationPlayer, AnimationSoundPlayer], (e) => {

        if(e.AnimationPlayer.currentFrameId === e.AnimationSoundPlayer.lastFrame)
            return;

        if( e.AnimationSoundPlayer.lastAnimation != e.AnimationPlayer.animation)
            e.AnimationSoundPlayer.lastFrame = -1

        const anim = animations[ e.AnimationPlayer.animation]
        if(!anim || !anim.sounds)
            return
        anim.sounds
            .filter(s=>s.frame <= e.AnimationPlayer.currentFrameId && s.frame > e.AnimationSoundPlayer.lastFrame )
            .forEach(s=>{

               // console.log("play sound",s.soundId)

                soundEvents[s.soundId].play();
            })

        e.AnimationSoundPlayer.lastFrame = e.AnimationPlayer.currentFrameId
        e.AnimationSoundPlayer.lastAnimation = e.AnimationPlayer.animation
    })

    entities.forEach([BaseLevel], (e) => {

        if (e.BaseLevel.isColliding) {

            const vol = clamp01(invLerp(40, 60, e.BaseLevel.collisionVelocity))
            if (vol > 0) {
                soundEvents[e.BaseLevel.collisionSound].play({
                    volume: vol
                })
            }
        }
    })

    entities.forEach([Button], (e) => {
        const sounds = Object.fromEntries([
            [ButtonEvent.Down, "button-push"],
            [ButtonEvent.DownWhileHolding, "button-push"],
            [ButtonEvent.UpWhileHolding, "button-release"],
            [ButtonEvent.UpCanceled, "button-release"],
            [ButtonEvent.UpClicked, "button-release"],
        ])
        soundEvents[sounds[e.Button.event]]?.play()
    })

    entities.forEach([Crank], (e) => {

        const da = deltaAngle(e.Rotation.value, e.Crank.lastAngle)
        if (Math.abs(da) > .08) {

            if (e.Crank.locked) {
                if(soundEvents["crank-locked"])
                     soundEvents["crank-locked"].play()
            } else {

                if(soundEvents["crank-unlocked"])
                 soundEvents["crank-unlocked"].play()
            }
            e.Crank.lastAngle = e.Rotation.value
        }
    })


    entities.forEach([PumpSound, PumpPressure], (e) => {

        if (e.PumpSound.pumpInstance === undefined) {
            e.PumpSound.pumpInstance = soundEvents["pump"].play({volume:0})
        }

        const pressureChangeSpeed = (e.PumpPressure.pressure - e.PumpPressure.lastPressure) / time.deltaTime;

        const vol = pressureChangeSpeed > 0 ? clamp01(invLerp(2, 10, pressureChangeSpeed*50)) : 0;
        const rate = e.PumpPressure.pressure;
        e.PumpSound.pumpInstance.setVolume(vol, .3)
        e.PumpSound.pumpInstance.setRate(rate, .3)
    })

    entities.forEach([PumpSound, PumpPressure], (e) => {

        if (e.PumpSound.releaseInstance === undefined) {
            e.PumpSound.releaseInstance = soundEvents["pump_release"].play({volume:0})
        }

        const pressureChangeSpeed = (e.PumpPressure.pressure - e.PumpPressure.lastPressure) / time.deltaTime;

        const vol = pressureChangeSpeed < 0 ? Math.abs(-pressureChangeSpeed*25) : 0;
        const rate = e.PumpPressure.pressure;

        e.PumpSound.releaseInstance.setVolume(vol, .3)
        e.PumpSound.releaseInstance.setRate(rate, .3)
    })
    entities.forEach([PumpSound, MoveGrabState, Position], (e) => {

        if (e.PumpSound.moveInstance === undefined) {
            e.PumpSound.moveInstance = soundEvents["pump-move"].play({volume:0})
        }

        const speedNorm = clamp01(invLerp(0,10, Math.abs(e.MoveGrabState.speed.y)))
        e.PumpSound.moveInstance.setVolume(speedNorm, .3)
        e.PumpSound.moveInstance.setRate(speedNorm, .3)
    })

   /* entities.forEach([PumpSound, MoveGrabState, Position], (e) => {

        const posNormalized = invLerp(e.MoveGrabState.rect.minY, e.MoveGrabState.rect.maxY, e.Position.value.y)

        const isTouching = e.MoveGrabState.pointer !== undefined && (posNormalized >= 0.999 || posNormalized <= 0.0001)
        if (isTouching && !e.PumpSound.wasTouching) {
            soundEvents["pump-touch"].play({
                volume: invLerp(0,5,Math.abs(e.MoveGrabState.speed.y))
            })
        }

        e.PumpSound.wasTouching = isTouching
    })*/

    entities.forEach([TensionSound, Input], (e) => {

        if (e.TensionSound.instancePump === undefined) {
            e.TensionSound.instancePump = soundEvents["tension_pump"].play({volume:0})
        }

        const tension = clamp01(invLerp(.6,1.0, e.Input.input))
        e.TensionSound.instancePump.setVolume(tension, .3)
        e.TensionSound.instancePump.setRate(tension, .3)
    })
    entities.forEach([TensionSound, Input], (e) => {

        if (e.TensionSound.instanceRelease === undefined) {
            e.TensionSound.instanceRelease = soundEvents["tension_release"].play({volume:0})
        }

        let vol = clamp01(Math.min(invLerp(0,.01, e.Input.input), invLerp(.25,.01, e.Input.input)))
        if( e.Input.input >= e.Input.lastInput)
            vol = 0

        const rate = clamp01(invLerp(0,.1, e.Input.input))
        e.TensionSound.instanceRelease.setVolume(vol, .3)
        e.TensionSound.instanceRelease.setRate(rate, .3)
    })


}